import { t } from '@procumi/shared';
import { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';

/**
 * Întrebarea dinaintea unei acțiuni care nu se mai poate desface, în fereastra
 * aplicației, nu în cea a browserului („localhost spune…”, OK / Cancel).
 *
 * Se cheamă ca `window.confirm`, dar întoarce o promisiune: `if (!(await
 * ask({...}))) return;`. Fereastra trăiește în rădăcina ei React, lângă
 * aplicație, ca s-o poată deschide oricine, și peste un dialog deja deschis
 * (un <dialog> modal nou urcă deasupra celorlalte).
 */
export interface AskOptions {
  title: string;
  /** ce se întâmplă, pe scurt; poate lipsi când titlul spune tot */
  message?: string;
  /** verbul acțiunii: „Șterge”, „Anulează cererea” — nu „OK” */
  confirm: string;
  /** implicit „Renunță” */
  cancel?: string;
  /** acțiune distructivă: butonul e roșu */
  danger?: boolean;
}

export function ask(opts: AskOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    const done = (answer: boolean) => {
      root.unmount();
      host.remove();
      resolve(answer);
    };
    root.render(<AskDialog {...opts} onAnswer={done} />);
  });
}

function AskDialog({
  title,
  message,
  confirm,
  cancel = t('Renunță'),
  danger = false,
  onAnswer,
}: AskOptions & { onAnswer: (answer: boolean) => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const answered = useRef(false);
  const answer = (v: boolean) => {
    if (answered.current) return;
    answered.current = true;
    onAnswer(v);
  };

  useEffect(() => {
    const d = ref.current!;
    d.showModal();
    // Escape înseamnă „renunță”.
    const onCancel = (e: Event) => {
      e.preventDefault();
      answer(false);
    };
    d.addEventListener('cancel', onCancel);
    return () => d.removeEventListener('cancel', onCancel);
  }, []);

  return (
    <dialog ref={ref} className="ask" aria-labelledby="ask-title">
      <div className="dlg-h">
        <h2 id="ask-title">{title}</h2>
      </div>
      {message ? <div className="dlg-b ask-b">{message}</div> : null}
      <div className="dlg-f">
        {/* Butonul sigur primește focusul: un Enter scăpat nu șterge nimic. */}
        <button className="btn" autoFocus onClick={() => answer(false)}>
          {cancel}
        </button>
        <button className={`btn ${danger ? 'danger' : 'primary'}`} onClick={() => answer(true)}>
          {confirm}
        </button>
      </div>
    </dialog>
  );
}
